import { AppNavPaths } from '../Common';

/**
 * The manual setup tasks needed to take a freshly-created tenant (already seeded
 * via the tenant copy) to the point where a job can be created, in order.
 *
 * Single source of truth for the post-create onboarding checklist, the reopen
 * button, and the per-tenant checklist icon (later steps).
 *
 * Each step:
 *  - key:         stable identifier (saved progress + completion detection)
 *  - category:    grouping shown in the modal
 *  - label:       short task name
 *  - description: what the task covers
 *  - navPath:     where "Set up" navigates (a route string, or a function of the
 *                 new tenant id for tenant-scoped pages)
 *
 * Note: the app labels these group pages counter-intuitively —
 *  "Resource Groups" is /userGroups and "Role Groups" is /groups
 *  (see NavMenu/constants.js).
 */
export const ONBOARDING_STEP_CATEGORIES = [
    'Roles & Groups',
    'Organization',
    'Resources',
    'Contracts',
];

export const ONBOARDING_STEPS = [
    {
        key: 'roles',
        category: 'Roles & Groups',
        label: 'Import customer roles',
        description: 'Import positions and roles when possible, then adjust permissions as needed.',
        navPath: `${AppNavPaths.SupportTools}/import-customer-data`,
    },
    {
        key: 'resource-groups',
        category: 'Roles & Groups',
        label: 'Set up resource groups',
        description: 'Create the resource groups used to organize users and their access.',
        navPath: AppNavPaths.UserGroups,
    },
    {
        key: 'role-groups',
        category: 'Roles & Groups',
        label: 'Set up role groups',
        description: 'Create the role groups (positions) — flaggers, office admin, supervisors, payroll clerk, etc.',
        navPath: AppNavPaths.Groups,
    },
    {
        key: 'billing-company',
        category: 'Organization',
        label: 'Set up billing company',
        description: 'Create the billing company used to invoice this tenant\'s work.',
        navPath: AppNavPaths.Organizations,
    },
    {
        key: 'payroll-company',
        category: 'Organization',
        label: 'Set up payroll company',
        description: 'Create the payroll company used to process this tenant’s payroll.',
        navPath: AppNavPaths.Organizations,
    },
    {
        key: 'dispatch-company',
        category: 'Organization',
        label: 'Set up dispatch company',
        description: 'Create the dispatch office(s) — employees and equipment are each assigned to one.',
        navPath: AppNavPaths.Organizations,
    },
    {
        key: 'customer-company',
        category: 'Organization',
        label: 'Import customer organizations',
        description: 'Import customer organizations before creating contracts.',
        navPath: `${AppNavPaths.SupportTools}/import-customer-data`,
    },
    {
        key: 'employees',
        category: 'Resources',
        label: 'Import employees',
        description: 'Add the employees for this tenant, each assigned to a dispatch office.',
        navPath: `${AppNavPaths.SupportTools}/import-customer-data`,
    },
    {
        key: 'equipment-types',
        category: 'Resources',
        label: 'Create equipment types',
        description: 'Barricades, arrow boards, AFADs, and any other equipment categories the tenant uses.',
        navPath: AppNavPaths.EquipmentTypes,
    },
    {
        key: 'equipment',
        category: 'Resources',
        label: 'Import equipment',
        description: 'Add the equipment for this tenant, each with an equipment type and branch (dispatch office).',
        navPath: `${AppNavPaths.SupportTools}/import-customer-data`,
    },
    {
        key: 'charge-types',
        category: 'Resources',
        label: 'Import charge types',
        description: 'Import charge types with billing units, usage, and resource type mappings.',
        navPath: `${AppNavPaths.SupportTools}/import-customer-data`,
    },
    {
        key: 'contracts',
        category: 'Contracts',
        label: 'Create contracts',
        description: 'Lastly, create the contracts so jobs can be scheduled against them.',
        navPath: AppNavPaths.Contracts,
    },
];
